import { useMemo } from 'react'
import { useCategories } from './useCategories.ts'
import { useTickers } from './useTickers.ts'
import type { Category } from '../types.ts'

type UseCategoriesTickersExportResult = {
  generateExport: () => string
  isLoading: boolean
  error: string
}

type CategoryPathRow = {
  id: number
  name: string
  slug: string
  path: string
  parentPath: string
}

export function useCategoriesTickersExport(userId: number): UseCategoriesTickersExportResult {
  const categoriesQuery = useCategories(userId)
  const tickersQuery = useTickers(userId)

  const categoryRows = useMemo(() => {
    const categories = categoriesQuery.data ?? []
    const byId = new Map(categories.map((category) => [category.id, category]))
    const pathCache = new Map<number, string>()

    function getCategoryPath(category: Category): string {
      const cachedPath = pathCache.get(category.id)
      if (cachedPath) return cachedPath

      const parent = category.parent_id == null ? null : byId.get(category.parent_id) ?? null
      const path = parent ? `${getCategoryPath(parent)}/${category.slug}` : category.slug
      pathCache.set(category.id, path)
      return path
    }

    return categories
      .map<CategoryPathRow>((category) => {
        const path = getCategoryPath(category)
        const parent = category.parent_id == null ? null : byId.get(category.parent_id) ?? null

        return {
          id: category.id,
          name: category.name,
          slug: category.slug,
          path,
          parentPath: parent ? getCategoryPath(parent) : '',
        }
      })
      .sort((a, b) => a.path.localeCompare(b.path))
  }, [categoriesQuery.data])

  const tickerRows = useMemo(() => {
    const tickers = tickersQuery.data ?? []
    const categoryPathById = new Map(categoryRows.map((category) => [category.id, category.path]))

    return tickers
      .map((ticker) => ({
        ticker: ticker.ticker,
        categoryPath:
          ticker.category_id == null ? '' : (categoryPathById.get(ticker.category_id) ?? ''),
      }))
      .sort((a, b) => a.ticker.localeCompare(b.ticker))
  }, [categoryRows, tickersQuery.data])

  const exportText = useMemo(() => {
    const lines = [
      '### my-tickers',
      '### export-categories-tickers',
      '### categories',
      'name\tslug\tpath\tparent_path',
      ...categoryRows.map(
        (category) =>
          `${category.name}\t${category.slug}\t${category.path}\t${category.parentPath}`,
      ),
      '### tickers',
      'ticker\tcategory_path',
      ...tickerRows.map((ticker) => `${ticker.ticker}\t${ticker.categoryPath}`),
    ]

    return lines.join('\n')
  }, [categoryRows, tickerRows])

  return {
    generateExport: () => exportText,
    isLoading: categoriesQuery.isLoading || tickersQuery.isLoading,
    error: categoriesQuery.error?.message ?? tickersQuery.error?.message ?? '',
  }
}
